import Box from '@mui/material/Box';
import type {
  GridCallbackDetails,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import type { SxProps, Theme } from '@mui/material';

import { checkIfIsArray, invariant } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { NoData } from '../Illustrations/NoData';
import { DataGrid } from '../DataGrid';
import { Loading } from '../Loading';

import { useInitialGridState } from './hooks/useInitialGridState.hook';
import { useFindAllResolvableServicesQuery } from './hooks/useFindAllResolvableServicesQuery.hook';
import { getColumns } from './get-columns';

interface Props {
  readonly checkboxSelection?: boolean | undefined;
  readonly excludedIds?: readonly Id[];
  readonly onRowSelectionModelChange?:
    | ((
        rowSelectionModel: Readonly<GridRowSelectionModel>,
        details: Readonly<GridCallbackDetails>,
      ) => void)
    | undefined;
  readonly rowSelectionModel?: GridRowSelectionModel | undefined;
  readonly subsetIds?: readonly Id[];
  readonly sx?: SxProps<Theme>;
}

const DEFAULT_SX = { flexGrow: 1, minHeight: 0, width: '100%', pt: 2 };

export const ResolvableServiceList = ({
  checkboxSelection = false,
  excludedIds,
  onRowSelectionModelChange: handleRowSelectionModelChange,
  rowSelectionModel,
  subsetIds,
  sx = DEFAULT_SX,
}: Readonly<Props>) => {
  invariant(
    !checkboxSelection ||
      !!(handleRowSelectionModelChange && rowSelectionModel),
    'ResolvableServiceList: If checkboxSelection is true, onRowSelectionModelChange and rowSelectionModel must be provided.',
  );

  const { data, isSuccess } = useFindAllResolvableServicesQuery();

  const { genericStatuses } = useReferencesContext();

  const { initialGridState } = useInitialGridState();

  if (isSuccess) {
    const { findAllResolvableServices: resolvableServices } = data;

    const filteredResolvableServices =
      !excludedIds?.length && !subsetIds
        ? resolvableServices
        : resolvableServices.filter(
            (resolvableService) =>
              !excludedIds?.includes(resolvableService._id) &&
              (!subsetIds || subsetIds.includes(resolvableService._id)),
          );

    return !filteredResolvableServices.length ? (
      <NoData />
    ) : (
      <Box sx={checkIfIsArray(sx) ? sx : [sx]}>
        <DataGrid
          checkboxSelection={checkboxSelection}
          columns={getColumns(genericStatuses)}
          initialState={initialGridState}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          rowSelectionModel={rowSelectionModel}
          rows={resolvableServices}
        />
      </Box>
    );
  }

  return <Loading />;
};
